// THE MASTHEAD (2026-10-30). One system for the whole site, the way a newspaper's front carries
// its name, its pillars and its sections. Two pieces, used by every surface:
//
//   THE PILLARS: Home, News, Edition. Three words beside the wordmark on the shelf, the one you
//     are on inked with a gold rule under it that slides when the page changes. On a phone the
//     shelf wraps and the three sit on their own line under the name.
//   THE SECTIONS: what this page is made of, as a row of chips under the shelf. Topics on the
//     news, the rooms of the home, the tools on a tool page, the parts of the front page on the
//     Edition. They scroll sideways on a phone and follow the scroll (a spy) on a long page.
//
// A pillar is a plain link, so it works from anywhere; a surface that can do better in place
// (the reader opens the Edition as a sheet, the home moves without a reload) says so through
// onPillar and returns true to keep the click.
import { useEffect, useLayoutEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from "react";
import { storeHref } from "./appPromo";
import { isNativeApp } from "./native";

export type Face = "home" | "news" | "edition";

export type Section = {
  id: string;
  label: string;
  /** A hash target on this page ("#around"), or a path. Omit when onClick does the work. */
  href?: string;
  on?: boolean;
  onClick?: () => void;
  /** How many stories sit behind this chip (the reader's topics). */
  count?: number;
};

export const PILLARS: { id: Face; label: string; href: string; hint: string }[] = [
  { id: "home", label: "Home", href: "/", hint: "What is happening around you, your numbers and your tools" },
  { id: "news", label: "News", href: "/read", hint: "Today's stories, as cards" },
  { id: "edition", label: "Edition", href: "/read#edition", hint: "The front page, set for this hour" },
];

/** Which pillar a location belongs to. The tools are rooms of the home; the game is part of the news. */
export function faceFromLocation(pathname: string, hash = ""): Face {
  if (pathname === "/" || /^\/more(\/|$)/.test(pathname)) return "home";
  if (/^\/read(\/|$)/.test(pathname) && /(?:^|[#&?])edition(?:[&=]|$)/.test(hash)) return "edition";
  return "news";
}

/** Scroll a hash target into view inside whatever scrolls (the window, or the Edition's sheet). */
export function scrollToSection(id: string): boolean {
  const el = document.getElementById(id);
  if (!el) return false;
  const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  return true;
}

export function Pillars({ active, onPillar }: { active: Face; onPillar?: (id: Face, e: ReactMouseEvent<HTMLAnchorElement>) => boolean | void }) {
  const ref = useRef<HTMLElement | null>(null);

  // The gold rule slides to the inked word: measured, not guessed, so it fits the word. It is a
  // 1px element scaled by a transform, so moving it never relays out the bar.
  useLayoutEffect(() => {
    const nav = ref.current;
    if (!nav) return;
    const place = () => {
      const on = nav.querySelector<HTMLElement>("a.on");
      if (!on) return;
      nav.style.setProperty("--ink-x", `${on.offsetLeft}px`);
      nav.style.setProperty("--ink-w", String(on.offsetWidth));
      nav.classList.add("measured");
    };
    place();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(place) : null;
    ro?.observe(nav);
    if (document.fonts?.ready) document.fonts.ready.then(place).catch(() => {});
    return () => ro?.disconnect();
  }, [active]);

  return (
    <nav className="site-pillars" aria-label="InSnip" ref={ref}>
      {PILLARS.map((p) => (
        <a
          key={p.id}
          href={p.href}
          className={p.id === active ? "on" : undefined}
          aria-current={p.id === active ? "page" : undefined}
          title={p.hint}
          onClick={(e) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
            if (onPillar?.(p.id, e)) e.preventDefault();
          }}
        >
          {p.label}
        </a>
      ))}
      <i className="site-pillars-ink" aria-hidden="true" />
      {/* THE APP, QUIETLY: one small word on every page of the site, to the store that fits the
          phone (or /get from a desktop). Never inside the app itself. It is not a pillar, so the
          gold rule never visits it. */}
      {!isNativeApp ? (
        <a className="site-app" href={storeHref()} target={storeHref().startsWith("/") ? undefined : "_blank"} rel={storeHref().startsWith("/") ? undefined : "noopener noreferrer"} title="The InSnip app, free on Google Play and the App Store">
          <span>App</span>
        </a>
      ) : null}
    </nav>
  );
}

export function Sections({
  sections,
  spy = false,
  onSection,
  label = "Sections",
}: {
  sections: Section[];
  /** Follow the scroll: light the section whose element is in view. Sections whose target is not on the page are not drawn. */
  spy?: boolean;
  onSection?: (id: string) => void;
  label?: string;
}) {
  const ref = useRef<HTMLElement | null>(null);
  const [spied, setSpied] = useState<string | null>(null);
  const [present, setPresent] = useState<Set<string> | null>(null);

  // The spy: which section is in view, and which exist at all (a room that appears once its
  // data lands is drawn once it is there).
  useEffect(() => {
    if (!spy || !sections.length) return;
    const ids = sections.map((s) => s.id);
    let io: IntersectionObserver | null = null;
    const seen = new Map<string, number>();
    const watch = () => {
      const here = new Set(ids.filter((id) => document.getElementById(id)));
      setPresent((prev) => (prev && prev.size === here.size && [...here].every((id) => prev.has(id)) ? prev : here));
      io?.disconnect();
      if (!("IntersectionObserver" in window)) return;
      io = new IntersectionObserver((entries) => {
        for (const e of entries) seen.set((e.target as HTMLElement).id, e.isIntersecting ? e.intersectionRatio + (e.boundingClientRect.top <= 200 ? 1 : 0) : 0);
        let best: string | null = null;
        let bestScore = 0;
        for (const id of ids) {
          const score = seen.get(id) ?? 0;
          if (score > bestScore) { best = id; bestScore = score; }
        }
        if (best) setSpied(best);
      }, { rootMargin: "-120px 0px -55% 0px", threshold: [0, 0.1, 0.25, 0.5] });
      for (const id of here) { const el = document.getElementById(id); if (el) io.observe(el); }
    };
    watch();
    const ticks = [400, 1200, 2500, 5000, 9000].map((ms) => window.setTimeout(watch, ms));
    return () => { io?.disconnect(); ticks.forEach(clearTimeout); };
  }, [spy, sections]);

  // Keep the lit chip in view on a phone.
  useEffect(() => {
    const strip = ref.current;
    const on = strip?.querySelector<HTMLElement>(".on");
    if (!strip || !on || strip.scrollWidth <= strip.clientWidth + 2) return;
    const target = on.offsetLeft - (strip.clientWidth - on.offsetWidth) / 2;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    strip.scrollTo({ left: Math.max(0, target), behavior: reduce ? "auto" : "smooth" });
  }, [spied, sections]);

  const shown = sections.filter((s) => !spy || !present || !s.href?.startsWith("#") || present.has(s.id));
  if (!shown.length) return null;
  const anyOn = shown.some((s) => s.on);

  return (
    <nav className="site-sections" aria-label={label} ref={ref}>
      {shown.map((s) => {
        // Before the spy has spoken, the first room is the one you are in.
        const on = anyOn ? !!s.on : spy ? (spied ?? shown[0]?.id) === s.id : false;
        if (s.href) {
          return (
            <a
              key={s.id}
              href={s.href}
              className={on ? "on" : undefined}
              aria-current={on ? "location" : undefined}
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                if (s.href?.startsWith("#")) {
                  if (scrollToSection(s.href.slice(1))) { e.preventDefault(); setSpied(s.id); }
                } else if (s.onClick) {
                  e.preventDefault(); // the surface moves itself (no reload)
                }
                s.onClick?.();
                onSection?.(s.id);
              }}
            >
              {s.label}
              {s.count != null ? <span className="ss-n">{s.count}</span> : null}
            </a>
          );
        }
        return (
          <button key={s.id} type="button" className={on ? "on" : undefined} aria-pressed={on} onClick={() => { s.onClick?.(); onSection?.(s.id); }}>
            {s.label}
            {s.count != null ? <span className="ss-n">{s.count}</span> : null}
          </button>
        );
      })}
    </nav>
  );
}
